import { useEffect, useRef } from 'react';

/**
 * Runs `flush` exactly once, when the component unmounts.
 *
 * Used by debounced-autosave surfaces to commit a pending edit when the user
 * navigates away inside the debounce window. Without this, the unmount cleanup
 * only clears the pending timer and the last edit is silently dropped (it never
 * persists, then the next load re-seeds the stale server value).
 *
 * The latest `flush` is tracked in a ref so the effect can keep empty deps (it
 * must fire only on real unmount, never on every render).
 *
 * Note: this covers in-app navigation, which unmounts the component. It does NOT
 * cover a hard tab/window close — that path needs `navigator.sendBeacon` or a
 * keepalive request, which our mutation client does not currently use.
 */
export function useFlushOnUnmount(flush: () => void): void {
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []);
}
